import {
  TokensRoot,
  ToastProvider,
  type ToastTone,
} from "@axisnode/ui";
import type { AuthFetchEnv } from "@axisnode/auth-client";
import type { ReactNode } from "react";
import { AuthGate } from "./AuthGate";
import { ShellLayout, type NavItem } from "./Layout";
import { SessionProvider, type PlatformSession } from "./session";

export type AppShellProps = {
  children: ReactNode;
  apiBaseUrl: string;
  title?: string;
  navItems?: NavItem[];
  /** Passed through to ShellLayout — see its docstring. */
  headerExtra?: ReactNode;
  requireAuth?: boolean;
  auth?: AuthFetchEnv;
  initialSession?: PlatformSession;
  assignLocation?: (url: string) => void;
  toastAutoDismissMs?: number;
};

/**
 * Composed Platform shell: tokens + session + auth-gate + layout + toast host.
 * Native Systems render their routes as `children` inside this frame.
 */
export function AppShell({
  children,
  apiBaseUrl,
  title,
  navItems,
  headerExtra,
  requireAuth = true,
  auth,
  initialSession,
  assignLocation,
  toastAutoDismissMs,
}: AppShellProps) {
  return (
    <TokensRoot>
      <ToastProvider autoDismissMs={toastAutoDismissMs}>
        <SessionProvider
          apiBaseUrl={apiBaseUrl}
          auth={auth}
          initialSession={initialSession}
        >
          <AuthGate requireAuth={requireAuth} assignLocation={assignLocation}>
            <ShellLayout title={title} navItems={navItems} headerExtra={headerExtra}>
              {children}
            </ShellLayout>
          </AuthGate>
        </SessionProvider>
      </ToastProvider>
    </TokensRoot>
  );
}

export type { ToastTone };
